'use client';

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { RatingStars } from '@eliadmin/core/src/components/nt/product/product-rating';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { SortMenu } from '@eliadmin/core/src/components/nt/category/listing/listing-sort';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import type { ReviewItem } from './review-items';

const LightboxComponent = lazy(() =>
  import('@eliadmin/core/src/components/lightbox/lightbox-component').then(module => ({
    default: module.LightboxComponent,
  })),
);

/** Figma 146:1374 shows three reviews and „Zobraziť ďalšie hodnotenia (125)“ of 128. */
const FIRST_PAGE = 3;
/** Reviews revealed per „Zobraziť ďalšie hodnotenia“ (lead decision). */
const PAGE = 10;

type Filter = 'all' | 'photo' | 1 | 2 | 3 | 4 | 5;
type Sort = 'newest' | 'oldest';

export type ReviewsListTexts = {
  /** „Filtrovať“ – accessible name of the chip group. */
  filter: string;
  /** „Všetky“. */
  all: string;
  /** „S fotkou“. */
  withPhoto: string;
  /** „Zoradiť“. */
  sort: string;
  /** „Najnovšie“. */
  newest: string;
  /** „Najstaršie“. */
  oldest: string;
  /** „Zobraziť ďalšie hodnotenia ({pocet})“. */
  more: string;
};

/** Figma „Chip“ (146:1374 toolbar): 40 px, r12, Body/M Semibold; the selected one #0D110D. */
const CHIP =
  'nt-body-m-semibold inline-flex h-10 shrink-0 items-center gap-1 whitespace-nowrap rounded-xl border px-4 transition-colors focus-visible:shadow-focus focus-visible:outline-none';
const CHIP_ON = 'border-[#0D110D] bg-[#0D110D] text-white';
const CHIP_OFF = 'border-[#E2E5DF] bg-white text-[#0D110D] hover:bg-[#F5F6F3]';

/**
 * Figma „Reviews column“ (146:1374): the filter chips (all / per star value / with a photo, each
 * with its count; a chip only when it narrows the list), „Zoradiť: Najnovšie ⌄“, the review rows
 * and „Zobraziť ďalšie hodnotenia (n)“ – n = the rest of the current filter; the revealed reviews
 * take the focus (the first of them). A filter or sort change starts at the first page again. Not
 * rendered from Figma (no API data): „Overené nákupy (n)“, „Overený nákup“, pros and cons,
 * „Bolo hodnotenie užitočné?“ and „Nahlásiť“. Below 768 px the chips scroll sideways.
 */
export function ReviewsList({
  reviews,
  starLabels,
  texts,
}: {
  reviews: ReviewItem[];
  /** „Hodnotenie 1/5“ … „Hodnotenie 5/5“, index 0 = 1 star. */
  starLabels: string[];
  texts: ReviewsListTexts;
}) {
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('newest');
  const [shown, setShown] = useState(FIRST_PAGE);
  const [photo, setPhoto] = useState<ReviewItem['photo']>(null);
  const items = useRef<(HTMLElement | null)[]>([]);
  /** Index of the first review revealed by the last „Zobraziť ďalšie“ – focused once rendered. */
  const focusFrom = useRef<number | null>(null);

  const chips = useMemo(() => {
    const total = reviews.length;
    const list: { value: Filter; count: number; label: string; srLabel?: string }[] = [
      { value: 'all', count: total, label: texts.all },
    ];
    for (const stars of [5, 4, 3, 2, 1] as const) {
      const count = reviews.filter(review => review.stars === stars).length;
      list.push({ value: stars, count, label: String(stars), srLabel: starLabels[stars - 1] });
    }
    list.push({
      value: 'photo',
      count: reviews.filter(review => review.photo).length,
      label: texts.withPhoto,
    });
    return list.filter(
      chip => chip.value === 'all' || (chip.label && chip.count > 0 && chip.count < total),
    );
  }, [reviews, starLabels, texts.all, texts.withPhoto]);

  const visible = useMemo(() => {
    const matching = reviews.filter(review =>
      filter === 'all' ? true : filter === 'photo' ? !!review.photo : review.stars === filter,
    );
    return matching.sort((a, b) => (sort === 'newest' ? b.time - a.time : a.time - b.time));
  }, [filter, reviews, sort]);
  const rest = Math.max(0, visible.length - shown);

  useEffect(() => {
    if (focusFrom.current === null) return;
    items.current[focusFrom.current]?.focus();
    focusFrom.current = null;
  }, [shown]);

  /** A different filter or order lists from the first page again (the same one keeps the list). */
  const choose = (next: { filter?: Filter; sort?: Sort }) => {
    if ((next.filter ?? filter) === filter && (next.sort ?? sort) === sort) return;
    if (next.filter !== undefined) setFilter(next.filter);
    if (next.sort !== undefined) setSort(next.sort);
    setShown(FIRST_PAGE);
  };

  const sortOptions: { value: Sort; label: string }[] = [
    { value: 'newest', label: texts.newest },
    { value: 'oldest', label: texts.oldest },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        {chips.length > 1 ? (
          <div
            role="group"
            aria-label={texts.filter || undefined}
            className="hide-scroll-bar -mx-4 -my-1 flex gap-2 overflow-x-auto px-4 py-1 sm:-mx-6 sm:px-6 md:m-0 md:flex-1 md:flex-wrap md:overflow-visible md:p-0"
          >
            {chips.map(chip => (
              <button
                key={chip.value}
                type="button"
                aria-pressed={filter === chip.value}
                aria-label={chip.srLabel ? `${chip.srLabel} (${chip.count})` : undefined}
                onClick={() => choose({ filter: chip.value })}
                className={cn(CHIP, filter === chip.value ? CHIP_ON : CHIP_OFF)}
              >
                {chip.label}
                {chip.srLabel ? <Icon name="star" size={14} /> : null}
                {` (${chip.count})`}
              </button>
            ))}
          </div>
        ) : null}
        {reviews.length > 1 && texts.newest && texts.oldest ? (
          <SortMenu
            label={texts.sort ? `${texts.sort}:` : ''}
            value={sort}
            options={sortOptions}
            onChange={value => choose({ sort: value })}
            valueClassName="nt-body-m-bold"
            className="shrink-0 gap-1.5 self-start md:ml-auto md:mt-2.5"
          />
        ) : null}
      </div>

      <ul role="list" className="flex flex-col gap-6">
        {visible.slice(0, shown).map((review, index) => (
          <li key={review.key}>
            <article
              ref={node => {
                items.current[index] = node;
              }}
              tabIndex={-1}
              className="flex flex-col gap-4 border-b border-[#E2E5DF] pb-6 focus-visible:shadow-focus focus-visible:outline-none"
            >
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="nt-body-m-bold flex size-11 shrink-0 items-center justify-center rounded-full bg-[#F6FBF7] text-[#0E6A20]"
                >
                  {review.initials}
                </span>
                <p className="nt-body-l-bold min-w-0 flex-1 break-words text-[#0D110D]">
                  {review.name}
                </p>
                {review.date ? (
                  <time dateTime={review.date.iso} className="nt-body-m shrink-0 text-[#5D655D]">
                    {review.date.text}
                  </time>
                ) : null}
              </div>
              <div className="flex flex-col gap-3">
                {review.rating ? (
                  <RatingStars rating={review.rating} label={review.ratingLabel} size={18} />
                ) : null}
                {review.text ? (
                  <p className="nt-body-l whitespace-pre-line break-words text-[#363D36]">
                    {review.text}
                  </p>
                ) : null}
              </div>
              {review.photo ? (
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-label={review.photo.alt || undefined}
                  onClick={() => setPhoto(review.photo)}
                  className="relative size-[88px] cursor-zoom-in overflow-hidden rounded-xl border border-[#E2E5DF] bg-white transition-colors hover:border-[#CDD2CA] focus-visible:shadow-focus focus-visible:outline-none"
                >
                  <Image
                    src={review.photo.url}
                    alt=""
                    fill
                    sizes="88px"
                    className="object-contain"
                  />
                </button>
              ) : null}
            </article>
          </li>
        ))}
      </ul>

      {rest > 0 && texts.more ? (
        <Button
          variant="outline"
          size="md"
          onClick={() => {
            focusFrom.current = shown;
            setShown(shown + PAGE);
          }}
          className="self-center"
        >
          {fillTemplate(texts.more, { pocet: rest })}
        </Button>
      ) : null}

      {photo ? (
        <Suspense fallback={null}>
          <LightboxComponent
            isOpen
            setIsOpen={open => {
              if (!open) setPhoto(null);
            }}
            images={[photo]}
            zoom={{ maxZoomPixelRatio: 8 }}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
